import * as Button from '../../../components/button/button';
import { Icons } from '../../../components/icons/icons';
import * as Page from '../../../components/content/page-header';
import { Link } from 'react-router-dom';
import { Route, Routes, useMatch, useParams, useNavigate, useLocation } from 'react-router';
import { General } from './tabs/general-editor';
import { DrawingInfo } from './tabs/drawing-info-editor';
import { FieldsList } from './tabs/fields-list';
import { useQuery } from '../../../hooks/use-query';
import { LayerExport, MapLayer } from '../types/map-layer';
import { Metadata } from './tabs/metadata-editor';
import { FieldsEditor } from './tabs/fields-editor';
import { useIsMutating } from 'react-query';
import { Translations } from './tabs/translations';
import { Animation } from './tabs/animation-editor';
import { LightBlack } from '../../../components/button/button';
import { createJSONFile } from '../../../utils/json-file';
import React from 'react';

export const MapLayerEditor = (): JSX.Element => {
  const location = useLocation();
  const params = useParams();
  const id = params.id as string;
  const navigate = useNavigate();
  const mapLayer = useQuery<MapLayer>({ path: `/map-layers/${id}`, options: { enabled: id !== 'new' } });
  const exportMapLayer = useQuery<LayerExport>({ path: `/map-layers/${id}/export`, options: { enabled: false } });
  const isMutating = useIsMutating();

  const onLayerExport = async (): Promise<void> => {
    const result = await exportMapLayer.refetch();
    createJSONFile((result.data?.layer?.id as string || 'layer-id'), JSON.stringify(result.data));
  };

  return <div>
    <div className="row">
      <div className="col-9 bg-light left-col">
        <Page.Header>
          <div className="breadcrumb">
            <span className="breadcrumb-text">
               <span>Map Layers /</span><span> {params.id === 'new' ? 'New' : mapLayer.data?.name as string} /</span><span className="text-dark-blue"> {params.id === 'new' ? 'Add' : 'Edit'}</span>
            </span>
          </div>
          <Page.Title>
            <Link to="/map-layers" className="page-title-link">
              <Icons.LeftArrow className="page-header-back" />
              Edit Map Layer
            </Link>
          </Page.Title>
        </Page.Header>
        <Tabs />
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
           <Button.Primary form="current-form" id="1" type="submit" disabled={isMutating}>
            {id === 'new' ? <><Icons.Add />Add</>: 'Update'}
           </Button.Primary>
          </div>
          {location.pathname.endsWith('/translations') && (
            <div className="mb-3">
              <LightBlack form="translations-form" type="submit" disabled={isMutating}>
                Translate
              </LightBlack>
            </div>
          )}
          <div className="mb-3">
            <LightBlack onClick={onLayerExport} disabled={exportMapLayer.isLoading || isMutating}>
              Export
            </LightBlack>
          </div>
          <div className="mb-3">
             <Button.Tertiary type="submit" disabled={isMutating} onClick={(): void => navigate('/map-layers')}>
              Cancel
             </Button.Tertiary>
          </div>
        </div>
      </div>
    </div>
  </div>;
};

const tabItems = [
  { path: '', text: 'General' },
  { path: '/drawing-info', text: 'Drawing info' },
  { path: '/animation', text: 'Animation' },
  { path: '/metadata', text: 'Metadata' },
  { path: '/fields', text: 'Fields' },
  { path: '/translations', text: 'Translations' },
];

const Tabs = (): JSX.Element => {
  const params = useParams();
  const id = params.id as string;
  const path = `/map-layers/${id}`;

  return <>
    <div className="tabs">
      {tabItems.map((tab, index: number) => {
        const pathname = `${path}${tab.path}`;
        const match = useMatch(pathname);
        return <span key={tab.path} className="d-flex align-items-center">
          {id === 'new' && index !== 0
            ? <span className={'tab-item disabled'}>{tab.text}</span>
            : <Link to={pathname} className={`tab-item ${match ? 'tab-item-selected' : ''}`}>{tab.text}</Link>}
          {index !== tabItems.length - 1 ? <span className="tab-separator" /> : null}
        </span>;
      })}
    </div>
    <div className="separator" />
      <Routes>
        <Route path="/" element={<General />} />
        <Route path="/drawing-info" element={<DrawingInfo />} />
        <Route path="/animation" element={<Animation />} />
        <Route path="/metadata" element={<Metadata />} />
        <Route path="/fields" element={<FieldsList />} />
        <Route path="/fields/:fieldId/editor" element={<FieldsEditor />} />
        <Route path="/translations" element={<Translations />} />
      </Routes>
  </>;
};
